import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { Uuid } from '~/types/Uuid';

import { testIsTestCaseNotExecuted } from '../fields/utils';
import { PromptModelTestCase } from '../types/deprecated';

export const useBulkExecuteModelTestCases = ({
  testCases,
  modelId,
  version,
}: {
  testCases: PromptModelTestCase[];
  modelId: Uuid;
  version: number;
}) => {
  const intl = useIntl();
  const MAX_RETRIES = 10;

  const keyCountRef = useRef<{ key: string; count: number }>({
    key: '',
    count: 0,
  });
  const { RETRY_ERROR } = useErrors();

  const { toast } = useToast();

  const [executePromptModelTestCases] =
    api.endpoints.executePromptModelTestCases.useMutation();

  const executeQueryKey = testCases // for determining effect deps to rerun bulk execute
    .filter(testIsTestCaseNotExecuted)
    .map((testCase) => testCase.number)
    .join('-');

  useEffect(() => {
    if (!executeQueryKey) {
      return;
    }

    if (keyCountRef.current.key !== executeQueryKey) {
      keyCountRef.current = { key: executeQueryKey, count: 0 };
    }

    const executeCount = keyCountRef.current.count;

    if (executeCount > MAX_RETRIES) {
      toast({
        message: intl.formatMessage({
          id: 'useBulkExecuteModelTestCases.evaluationFailed',
          defaultMessage:
            'Failed to evaluate results. Please refresh the page.',
          description: 'Error message when evaluation fails after max retries',
        }),
        status: 'danger',
      });
    } else {
      keyCountRef.current.count += 1;
      executePromptModelTestCases({
        modelId,
        version,
      })
        .unwrap()
        .catch((error) => {
          if (error.response) {
            const message = error.response.data.detail ?? RETRY_ERROR;
            switch (error.response.status) {
              case 409:
              case 429:
                break; // intentionally swallow rate-limit or conflict errors
              default:
                toast({
                  key: modelId + version,
                  message,
                  status: 'danger',
                });
                break;
            }
          }
        });
    }
  }, [executeQueryKey, modelId, version]);
};
